<template>
  <div
    v-if="canGetBoost && unleash.isEnabled('SubscriptionBoost')"
    :class="[$style.base, { [$style.rounded]: rounded }]"
    v-test="'boost-banner'"
  >
    <BaseText color="inverted">
      {{ $t('boost.description', brandName()) }}
    </BaseText>
    <BaseButton
      @click="onModalOpen"
      v-test="'btn-get-boost'"
    >
      {{ $t('boost.btn_text') }}
    </BaseButton>
  </div>
  <BoostModal
    v-if="showModal"
    :showErrorMessage="showErrorMessage"
    :isSubmitting="isSubmitting"
    @submit="getBoost"
    @close="onModalClose"
  />
</template>

<script
  lang="ts"
  setup
>
import { useMutation } from '@vue/apollo-composable';
import gql from 'graphql-tag';
import { storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import { brandName } from '@/helpers/brand-name';
import { flash } from '@/helpers/ui';
import { useCompanyStore } from '@/stores/company';
import { useUserStore } from '@/stores/user';
import unleash from '@/unleash';
import BoostModal from './BoostModal.vue';

defineProps<{
  rounded?: boolean;
}>();
const emit = defineEmits(['success']);

const { t } = useI18n();

const { canGetBoost } = storeToRefs(useCompanyStore());

const showModal = ref(false);
const showErrorMessage = ref(false);
const isSubmitting = ref(false);

const CREATE_BOOST_EXTENSION = gql`
  mutation createBoostExtension($input: CreateBoostExtensionInput!) {
    createBoostExtension(input: $input) {
      errors {
        type
      }
    }
  }
`;

const { mutate } = useMutation(CREATE_BOOST_EXTENSION, () => ({
  variables: {
    input: {},
  },
}));

const onModalOpen = () => {
  showModal.value = true;
};

const onModalClose = () => {
  showModal.value = false;
};

const onBoostSuccess = () => {
  const { getUser } = useUserStore();
  getUser().then(() => {
    emit('success');
  });
};

const getBoost = () => {
  isSubmitting.value = true;
  showErrorMessage.value = false;
  mutate()
    .then(
      ({
        data: {
          createBoostExtension: { errors },
        },
      }) => {
        if (errors) {
          showErrorMessage.value = true;
        } else {
          showModal.value = false;
          flash(t('boost.success_message'));
          onBoostSuccess();
        }
      },
    )
    .catch(() => {
      showErrorMessage.value = true;
    })
    .finally(() => {
      isSubmitting.value = false;
    });
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: $spacing;
  color: var(--color-bg);
  padding: $spacing;
  border-radius: 0 0 $radius $radius;

  @include theme('light') {
    background: linear-gradient(
      90deg,
      rgba(38, 37, 99, 1) 0%,
      rgba(76, 73, 197, 1) 50%,
      rgba(204, 255, 244, 1) 100%
    );
  }

  @include theme('dark') {
    background: linear-gradient(90deg, var(--color-primary-dark) 0%, var(--color-bg) 100%);
  }

  &.rounded {
    border-radius: $radius;
  }
}
</style>
